import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export default function PointTypesDialog({ open, onOpenChange, onChanged }) {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [release, setRelease] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/v2/itp-point-types").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!code.trim() || !name.trim()) { toast.error("Enter a short code and a name."); return; }
    setBusy(true);
    try {
      await api.post("/v2/itp-point-types", { code: code.trim().toUpperCase(), name: name.trim(), release_required: release });
      setCode(""); setName(""); setRelease(true);
      await load();
      onChanged?.();
      toast.success("Point type added.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add point type");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/v2/itp-point-types/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="point-types-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Custom point types</DialogTitle>
          <DialogDescription>
            Your company's own inspection point codes, e.g. SP — Stop Point. Available in every ITP template in your workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {items.length === 0 && <div className="rounded-md border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">No custom point types yet.</div>}
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm" data-testid={`point-type-row-${p.code}`}>
              <span className="w-12 font-mono text-xs font-bold">{p.code}</span>
              <span className="flex-1">{p.name}</span>
              {p.release_required && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Release required</span>}
              <Button variant="ghost" size="sm" className="h-6 px-1 text-rose-600" onClick={() => remove(p.id)} data-testid={`delete-point-type-${p.code}`}><Trash2 className="size-3.5"/></Button>
            </div>
          ))}
        </div>
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-[80px_1fr] gap-2">
            <div>
              <Label className="text-[11px]">Short code</Label>
              <Input className="mt-1 h-8 uppercase" maxLength={10} value={code} placeholder="SP"
                onChange={(e) => setCode(e.target.value)} data-testid="point-type-code-input"/>
            </div>
            <div>
              <Label className="text-[11px]">Name</Label>
              <Input className="mt-1 h-8" maxLength={100} value={name} placeholder="Stop Point"
                onChange={(e) => setName(e.target.value)} data-testid="point-type-name-input"/>
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox checked={release} onCheckedChange={(v) => setRelease(!!v)} data-testid="point-type-release-check"/>
            Release required before work continues
          </label>
          <Button size="sm" onClick={add} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="add-point-type-btn">
            <Plus className="mr-1.5 size-4"/>Add point type
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
